import { fontStack, addAlphaToHex } from "./utils";

export const palette = {
  white: "#ffffff",
  black: "#000000",

  gray: {
    25: "#fefefe",
    40: "#f1f3f4",
    50: "#f4f7f9",
    75: "#e9edef",
    90: "#e9e9e9",
    100: "#dde0e2",
    150: "#d4cfcf",
    200: "#caced1",
    250: "#aeb6ce",
    300: "#92979e",
    400: "#8E9AA7",
    500: "#7f858d",
    600: "#7B7F91",
    700: "#6E7C8A",
    750: "#5A6573",
    800: "#343434",
  },

  blue: {
    200: "#8CBFF4",
    300: "#8ABBF2",
    500: "#4690e2",
    600: "#2972C4",
    700: "#155EB0",
  },

  red: {
    300: "#FC8686",
    500: "#ee3f3e",
    550: "#e05453",
    600: "#DE2523",
    700: "#C71B1A",
  },

  orange: {
    300: "#FFC163",
    500: "#FFA318",
    600: "#F08811",
    700: "#CF7004",
  },

  purple: {
    300: "#C4ACFA",
    500: "#9268F3",
    600: "#754AD4",
    700: "#5F31C4",
  },

  green: {
    300: "#5AD6AD",
    500: "#0cba7f",
    550: "#37AC82",
    600: "#178F64",
    700: "#017147",
  },
};

export const paletteV2 = {
  white: "#ffffff",
  black: "#000000",
  red: {
    25: "#FFF4F4",
    50: "#FFD6D6",
    100: "#FFBEBE",
    200: "#FFA6A6",
    300: "#FC8686",
    400: "#F96E6E",
    500: "#EE3F3E",
    600: "#DE2523",
    700: "#C71B1A",
    800: "#B21110",
    900: "#9A0706",
  },
  grey: {
    0: "#F9FBFD",
    25: "#F9FBFD",
    50: "#F2F4F6",
    100: "#E2E6EA",
    200: "#D1D7DE",
    300: "#BEC6D0",
    500: "#8D9AA8",
    600: "#6F8390",
    800: "#3E4E5C",
    900: "#2C3847",
  },
  blue: {
    50: "#D5E7FC",
    100: "#B0D3F8",
    200: "#8CBFF4",
    300: "#8ABBF2",
    400: "#6CA7EE",
    500: "#4690E2",
    600: "#2972C4",
    700: "#155EB0",
    800: "#0E4B8D",
    900: "#07386A",
  },
  green: {
    50: "#CCF7E5",
    100: "#9BEED1",
    200: "#6AE5BD",
    300: "#5AD6AD",
    400: "#39C8A0",
    500: "#0CBA7F",
    600: "#178F64",
    700: "#017147",
    800: "#005A38",
    900: "#003F27",
  },
  orange: {
    25: "#FFFAEF",
    50: "#FFE7B0",
    100: "#FFD593",
    200: "#FFC876",
    300: "#FFC163",
    400: "#FFB04A",
    500: "#FFA318",
    600: "#F08811",
    700: "#CF7004",
    800: "#B15C00",
    900: "#944800",
  },
  purple: {
    50: "#F9FBFD",
    100: "#E6D9FF",
    200: "#DAC5FF",
    300: "#C4ACFA",
    400: "#AE93F5",
    500: "#754AD4",
    600: "#9268F3",
    700: "#5F31C4",
    800: "#491A8F",
    900: "#33045B",
  },
  cyan: {
    500: "#3A7983",
  },
};

const fontWeights = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
};

const fonts = {
  normal: fontStack(["Inter Variable", "sans-serif"]),
};

const breakpoints = ["320px"];

// todo: use rems, not pixels, so the font size is adjusted to a user default font size
const fontSizes = ["12px", "14px", "16px", "21px", "33px"];

const fontSizesV2 = {
  h1: "35px",
  h2: "29px",
  h3: "24px",
  h4: "20px",
  h5: "17px",
  h6: "14px",
  body: "14px",
  body2: "12px",
};

const colorsV2 = {
  ...paletteV2,

  accent: {
    fg: paletteV2.blue[500],
  },

  danger: {
    fg: paletteV2.red[500],
    bg: paletteV2.red[600],
  },

  success: {
    fg: paletteV2.green[500],
    bg: paletteV2.green[600],
  },

  info: {
    bg: paletteV2.blue[500],
  },

  fg: {
    default: paletteV2.grey[800],
    muted: paletteV2.grey[500],
  },

  canvas: {
    default: paletteV2.grey[50],
    subtle: paletteV2.grey[25],
    inset: paletteV2.white,
  },

  border: {
    default: addAlphaToHex(paletteV2.grey[200], 20),
    muted: addAlphaToHex(paletteV2.grey[200], 30),
  },
};

const colors = {
  ...palette,

  accent: {
    fg: palette.blue[500],
  },

  danger: {
    fg: palette.red[500],
    bg: palette.red[550],
  },

  success: {
    fg: palette.green[500],
    bg: palette.green[550],
  },

  info: {
    bg: palette.blue[500],
  },

  fg: {
    default: palette.gray[800],
    muted: palette.gray[500],
  },

  canvas: {
    default: palette.gray[50],
    subtle: palette.gray[25],
    inset: palette.white,
  },

  border: {
    default: addAlphaToHex(palette.gray[250], 20),
    muted: addAlphaToHex(palette.gray[250], 30),
    muted2: addAlphaToHex(palette.gray[250], 35),
    subtle: addAlphaToHex(palette.gray[90], 65),
  },
};

// todo: probably outlineV2 should be inside colors
const outlineV2 = {
  checkbox: addAlphaToHex(colorsV2.blue[50], 50),
  radioButton: addAlphaToHex(colorsV2.blue[50], 50),
  sliderThumb: addAlphaToHex(colorsV2.blue[50], 50),
  sliderThumbActive: addAlphaToHex(colorsV2.blue[50], 80),
};

const sizes = {
  width: {
    maxModalWidth: "1920px",
    sidebar: {
      collapsed: 73,
      expanded: 258,
    },
  },
  height: {
    tabBar: "48px",
    header: "69px",
  },
};

const dataGrid = {
  rowHeight: 40,
  headerRowHeight: 34,
  headerFilterRowHeight: 48,
};

const productDataGrid = {
  rowHeight: 60,
};

const text = {
  body1: {
    fontSize: fontSizes[2],
  },
  body2: {
    fontSize: fontSizes[1],
  },
};

const textV2 = {
  body1: {
    fontSize: fontSizesV2.body,
  },
  body2: {
    fontSize: fontSizesV2.body2,
  },
};

const shadow = {
  primary: "0 6px 30px 0 " + addAlphaToHex(palette.gray[600], 7),
  secondary: "4px 4px 6px 0 " + addAlphaToHex(palette.gray[150], 10),
};

const shadowV2 = {
  primary: `0px 0px 9px 0px ${addAlphaToHex(paletteV2.black, 15)}`,
  secondary: `0px 2px 15px 0px ${addAlphaToHex(paletteV2.black, 15)}`,
  tertiary: `0px 2px 25px 0px ${addAlphaToHex(paletteV2.black, 25)}`,
  sliderThumb: `0 0 0 3px ${outlineV2.sliderThumb}`,
  sliderThumbActive: `0 0 0 3px ${outlineV2.sliderThumbActive}`,
  error: `0px 0px 5px ${colorsV2.red[200]}`,
  default: `0px 1px 5px 0px ${addAlphaToHex(paletteV2.black, 15)}`,
};

const inputBorderV2 = {
  solid: {
    standard: {
      default: `1px solid ${colorsV2.grey[200]}`,
      error: `1px solid ${colorsV2.red[300]}`,
    },
    hover: {
      default: `1px solid ${colorsV2.grey[600]}`,
      error: `1px solid ${colorsV2.red[500]}`,
    },
    focused: {
      default: `1px solid ${colorsV2.blue[500]}`,
      error: `1px solid ${colorsV2.red[500]}`,
    },
    disabled: {
      default: `1px solid ${colorsV2.grey[200]}`,
      error: `1px solid ${colorsV2.grey[200]}`, // the plug;
    },
  },
  dashed: {
    standard: {
      default: `1px dashed ${colorsV2.grey[100]}`,
      error: `1px dashed ${colorsV2.red[200]}`,
    },
    hover: {
      default: `1px dashed ${colorsV2.grey[500]}`,
      error: `1px dashed ${colorsV2.red[400]}`,
    },
    focused: {
      default: `1px dashed ${colorsV2.blue[400]}`,
      error: `1px dashed ${colorsV2.red[400]}`,
    },
    disabled: {
      default: `1px dashed ${colorsV2.grey[100]}`,
      error: `1px dashed ${colorsV2.grey[100]}`, // the plug;
    },
  },
  transparent: `1px solid transparent`,
};

const inputColorV2 = {
  disabled: colorsV2.grey[600],
  normal: colorsV2.grey[900],
};

const inputWeightV2 = {
  disabled: fontWeights.normal,
  normal: fontWeights.medium,
};

const borderRadiusV2 = {
  primary: "6px",
  secondary: "3px",
  circle: "100%",
  fullRounded: "9999px",
};

const filter = {
  tooltipShadow: `drop-shadow(0px 16px 30px ${addAlphaToHex(
    palette.gray[600],
    20,
  )})`,
};

const zIndices = {
  dropdowns: 1000,
  popovers: 2000,
  tooltips: 3000,
  loadingOverlays: 5000,
  mainTabsDropdown: 5001,
  modalCloseButton: 6000,
  sidebarFloatingMenu: 7000,
  floatingSidebar: 6999,
  floatingSidebarOverlay: 6998,
  modals: 9000,
  notifications: 10000,
};

const config = {
  useBorderBox: true,
};

const animationTimingFunctionV2 = {
  dropdownArrowRotation: "cubic-bezier(0.16, 1, 0.3, 1)",
};

const animationDurationV2 = {
  dropdownArrowRotation: "400ms",
};

const size = {
  mobileS: "320px",
  mobileM: "375px",
  mobileL: "425px",
  tablet: "768px",
  laptop: "1024px",
  ipadProLandscape: "1366px",
  laptopL: "1440px",
  laptopXL: "1600px",
  desktop: "1860px",
  desktopL: "2560px",
};

const lessThanDevices = {
  mobileS: `(max-width: ${size.mobileS})`,
  mobileM: `(max-width: ${size.mobileM})`,
  mobileL: `(max-width: ${size.mobileL})`,
  tablet: `(max-width: ${size.tablet})`,
  laptop: `(max-width: ${size.laptop})`,
  laptopL: `(max-width: ${size.laptopL})`,
  laptopXL: `(max-width: ${size.laptopXL})`,
  desktop: `(max-width: ${size.desktop})`,
  desktopL: `(max-width: ${size.desktopL})`,
  ipadProLandscape: `(max-width: ${size.ipadProLandscape})`,
};

const greaterThanDevices = {
  mobileS: `(min-width: ${size.mobileS})`,
  mobileM: `(min-width: ${size.mobileM})`,
  mobileL: `(min-width: ${size.mobileL})`,
  tablet: `(min-width: ${size.tablet})`,
  laptop: `(min-width: ${size.laptop})`,
  ipadProLandscape: `(min-width: ${size.ipadProLandscape})`,
  laptopL: `(min-width: ${size.laptopL})`,
  laptopXL: `(min-width: ${size.laptopXL})`,
  desktop: `(min-width: ${size.desktop})`,
  desktopL: `(min-width: ${size.desktopL})`,
};

export const device = greaterThanDevices;

// todo: rename deviceV2 to media
export const deviceV2 = {
  lessThan: lessThanDevices,
  greaterThan: greaterThanDevices,
};

const inputLabelV2 = {
  fontSize: fontSizesV2.body,
  lineHeight: "21px",
  color: colorsV2.grey[800],
  fontWeight: fontWeights.normal,
};

const inputV2 = {
  paddingLeft: "11px",
  paddingRight: "11px",
  paddingTop: "7px",
  paddingBottom: "7px",
  lineHeight: "21px",
  fontSize: fontSizesV2.body,
};

const textAreaV2 = {
  ...inputV2,
  paddingTop: "5px", // why text area has different padding? Is it a design mistake?
  paddingBottom: "5px",
};

const selectV2 = {
  ...inputV2,
};

const inputV2Placeholder = {
  color: colorsV2.grey[600],
  fontWeight: fontWeights.normal,
};

const inputV2PlaceholderDisabled = {
  color: colorsV2.grey[300],
  fontWeight: fontWeights.normal,
};

const numberInputContainer = {
  fontSize: inputV2.fontSize,
  borderRadius: borderRadiusV2.primary,
};

const numberInput = {
  paddingRight: inputV2.paddingRight,
  paddingLeft: inputV2.paddingLeft,
  paddingTop: inputV2.paddingTop,
  paddingBottom: inputV2.paddingBottom,
};

const checkboxV2Color = {
  normal: colorsV2.grey[800],
  disabled: colorsV2.grey[600],
};

const numberInputUnit = {
  paddingRight: numberInput.paddingRight,
};

const linkV2 = {
  fontSize: fontSizesV2.body,
  lineHeight: "16.94px",
  color: colorsV2.blue[500],
  fontWeight: fontWeights.normal,
};

const skeleton = {
  baseColor: colors.gray[75],
  highlightColor: colors.gray[50],
};

export const theme = {
  fontSizes,
  fontSizesV2,
  breakpoints,
  fonts,
  fontWeights,
  colors,
  colorsV2,
  animationTimingFunctionV2,
  animationDurationV2,
  borderRadiusV2,
  outlineV2,
  shadow,
  shadowV2,
  sizes,
  text,
  textV2,
  zIndices,
  config,
  filter,
  inputBorderV2,
  inputColorV2,
  inputLabelV2,
  inputV2,
  textAreaV2,
  selectV2,
  numberInput,
  numberInputContainer,
  numberInputUnit,
  inputV2Placeholder,
  inputV2PlaceholderDisabled,
  device,
  deviceV2,
  linkV2,
  skeleton,
  dataGrid,
  productDataGrid,
  inputWeightV2,
  checkboxV2Color,
};

export type ExactTheme = typeof theme;

import "@emotion/react";

declare module "@emotion/react" {
  export interface Theme extends ExactTheme {}
}
